import React, { useEffect, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'

import { springs } from '../animation/tokens'
import { Button, PlainButton } from './button'

// ─────────────────────────────────────────────────────────────
// Cookie consent banner
//
// Custom Tailwind banner — deliberately not using a vendor SDK
// (OneTrust, Cookiebot, etc.) to keep cost down and bundle small.
// Two-button GDPR-compliant pattern:
//   - "Accept all"          → localStorage.ctrls_consent = 'all'
//   - "Necessary only"      → localStorage.ctrls_consent = 'necessary'
//
// The choice is read on mount; banner is hidden once a value is
// set. Clearing localStorage.ctrls_consent re-shows the banner.
//
// `getConsent()` is exported for the analytics loader so it can
// gate script injection on the user's choice.
//
// SSR safety: localStorage doesn't exist during build. We render
// nothing until the first effect runs (clientReady=true gate).
// AnimatePresence drives the entrance + exit animation using the
// shared `springs.gentle` token from animation/tokens.ts so the
// feel matches other reveals across the app.
// ─────────────────────────────────────────────────────────────

export type ConsentValue = 'all' | 'necessary' | null

const STORAGE_KEY = 'ctrls_consent'
const CONSENT_EVENT = 'ctrls:consent-changed'

export function getConsent(): ConsentValue {
  if (typeof window === 'undefined') return null
  const v = window.localStorage.getItem(STORAGE_KEY)
  if (v === 'all' || v === 'necessary') return v
  return null
}

export function setConsent(v: 'all' | 'necessary') {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(STORAGE_KEY, v)
  // Custom event so any listener (analytics loader, embedded widgets)
  // can react without a page reload.
  window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: v }))
}

export function CookieConsent() {
  const [clientReady, setClientReady] = useState(false)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    setClientReady(true)
    if (getConsent() == null) setVisible(true)
  }, [])

  function choose(v: 'all' | 'necessary') {
    setConsent(v)
    setVisible(false)
  }

  if (!clientReady) return null

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          role="dialog"
          aria-live="polite"
          aria-label="Cookie consent"
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 40, opacity: 0 }}
          transition={{ ...springs.gentle, opacity: { duration: 0.18 } }}
          className="fixed inset-x-0 bottom-0 z-50 flex flex-col justify-between gap-x-8 gap-y-4 border-t border-black/10 bg-white p-6 shadow-lg md:flex-row md:items-center lg:px-8 dark:border-white/10 dark:bg-mist-900"
        >
          <p className="max-w-4xl text-ctrl-sm text-black dark:text-white">
            Essential cookies keep this site running. With your okay, we'll also set analytics
            cookies so we can see what's useful and improve. See our{' '}
            <a
              href="/privacy-policy/"
              className="font-semibold text-ctrls underline-offset-2 hover:underline"
            >
              privacy policy
            </a>
            .
          </p>
          <div className="flex flex-none items-center gap-x-6">
            <Button type="button" color="filled" onClick={() => choose('all')}>
              Accept all
            </Button>
            <PlainButton type="button" onClick={() => choose('necessary')}>
              Reject all
            </PlainButton>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
